import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import styles from './AddShorts.module.css'; // Reusing the same CSS file
import { useCategories } from '../../../hooks/categories/useCategories';
import { useParams, useNavigate } from 'react-router-dom';
import { useShortById, useUpdateShort } from '../../../hooks/admin/useAdminShorts';

const EditShorts = () => {
  const { shortId } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    is_public: true,
    is_featured: false,
    duration: ''
  });
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [originalVideoUrl, setOriginalVideoUrl] = useState(null);
  const [originalThumbnailUrl, setOriginalThumbnailUrl] = useState(null);
  
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  const { categories = [] } = useCategories('Shorts');
  
  // Fetch short data using the hook
  const { shortData, isLoading: isLoadingShort, error: shortError } = useShortById(shortId);
  const { updateShort, isLoading: isUpdating } = useUpdateShort();

  // Load existing short data when available
  useEffect(() => {
    if (shortData) {
      setFormData({
        title: shortData.title || '',
        description: shortData.description || '',
        category_id: shortData.category_id || '',
        is_public: shortData.is_public !== undefined ? shortData.is_public : true,
        is_featured: shortData.is_featured !== undefined ? shortData.is_featured : false,
        duration: shortData.duration || ''
      });
      
      if (shortData.video_url) {
        setOriginalVideoUrl(shortData.video_url);
        setVideoPreview(shortData.video_url);
      }
      
      if (shortData.thumbnail_url) {
        setOriginalThumbnailUrl(shortData.thumbnail_url);
        setThumbnailPreview(shortData.thumbnail_url);
      }
    }
  }, [shortData]);

  // Handle errors from fetching short
  useEffect(() => {
    if (shortError) {
      toast.error(shortError?.response?.data?.message || 'Failed to load short data');
      navigate('/admin/shorts');
    }
  }, [shortError, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnail(file);
      setThumbnailPreview(URL.createObjectURL(file));
    }
  };

  const removeVideo = () => {
    setVideoFile(null);
    if (originalVideoUrl) {
      setVideoPreview(originalVideoUrl);
    } else {
      setVideoPreview(null);
    }
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  };

  const removeThumbnail = () => {
    setThumbnail(null);
    if (originalThumbnailUrl) {
      setThumbnailPreview(originalThumbnailUrl);
    } else {
      setThumbnailPreview(null);
    }
    if (thumbnailInputRef.current) {
      thumbnailInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Create FormData object for the request
    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key] !== undefined && formData[key] !== null) {
        formDataToSend.append(key, formData[key]);
      }
    });
    
    // Only append files if they've been changed
    if (videoFile) {
      formDataToSend.append('video', videoFile);
    }
    
    if (thumbnail) {
      formDataToSend.append('thumbnail', thumbnail);
    }

    try {
      await updateShort({ shortId, formData: formDataToSend });
      navigate('/admin/shorts'); // Navigate back to shorts list after successful update
    } catch (error) {
      console.error('Update failed:', error);
    }
  };

  if (isLoadingShort) {
    return <div className={styles.loading}>Loading short data...</div>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Edit Short</h1>
      
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.inputGroup}>
          <label htmlFor="title">Title *</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
          />
        </div>

        <div className={styles.inputGroup}>
          <label htmlFor="category">Category</label>
          <select
            id="category"
            name="category_id"
            value={formData.category_id}
            onChange={handleInputChange}
          >
            <option value="">Select Category</option>
            {categories?.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Optional duration field */}
        <div className={styles.inputGroup}>
          <label htmlFor="duration">Duration (seconds)</label>
          <input
            type="number"
            id="duration"
            name="duration"
            value={formData.duration}
            onChange={handleInputChange}
            min="1"
            step="0.1"
          />
        </div>

        <div className={styles.mediaSection}>
          <div className={styles.uploadBox}>
            <label>Video Upload</label>
            <input
              type="file"
              ref={videoInputRef}
              accept="video/*"
              onChange={handleVideoChange}
              className={styles.fileInput}
            />
            {!videoPreview ? (
              <div className={styles.uploadPlaceholder}>
                <Upload size={40} />
                <p>Click to upload new video</p>
              </div>
            ) : (
              <div className={styles.previewContainer}>
                <video src={videoPreview} controls className={styles.videoPreview} />
                <button
                  type="button"
                  onClick={removeVideo}
                  className={styles.removeButton}
                >
                  <X size={20} />
                </button>
              </div>
            )}
            {originalVideoUrl && !videoFile && (
              <p className={styles.noteText}>Using existing video. Upload new to replace.</p>
            )}
          </div>

          <div className={styles.uploadBox}>
            <label>Thumbnail</label>
            <input
              type="file"
              ref={thumbnailInputRef}
              accept="image/*"
              onChange={handleThumbnailChange}
              className={styles.fileInput}
            />
            {!thumbnailPreview ? (
              <div className={styles.uploadPlaceholder}>
                <ImageIcon size={40} />
                <p>Click to upload thumbnail</p>
              </div>
            ) : (
              <div className={styles.previewContainer}>
                <img src={thumbnailPreview} alt="Thumbnail preview" className={styles.thumbnailPreview} />
                <button
                  type="button"
                  onClick={removeThumbnail}
                  className={styles.removeButton}
                >
                  <X size={20} />
                </button>
              </div>
            )}
            {originalThumbnailUrl && !thumbnail && (
              <p className={styles.noteText}>Using existing thumbnail. Upload new to replace.</p>
            )}
          </div>
        </div>

        <div className={styles.checkboxGroup}>
          <label>
            <input
              type="checkbox"
              name="is_public"
              checked={formData.is_public}
              onChange={handleInputChange}
            />
            Make this short public
          </label>
          
          <label>
            <input
              type="checkbox"
              name="is_featured"
              checked={formData.is_featured}
              onChange={handleInputChange}
            />
            Feature this short
          </label>
        </div>

        <div className={styles.buttonGroup}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={() => navigate('/admin/shorts')}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isUpdating}
          >
            {isUpdating ? 'Updating...' : 'Update Short'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditShorts;